/*
  rbk-media-contrast.js
  ------------------------------------------------------------------
  Text that sits ON TOP of imagery must contrast with the PHOTO, not with the
  page theme. Theme ink (near-black in light mode) disappears on a dark image.

  For every element marked [data-rbk-media-target], we sample the average
  luminance of its <img> (bottom half, where overlay text usually sits) and set
  data-contrast="dark" (image is light -> use dark text) or "light" (image is
  dark -> use light text). Sections then resolve overlay text + veil from that.

  Robustness:
  - Sampling uses fetch(cors) -> createImageBitmap -> canvas, so the canvas is
    fed from a same-origin blob and is never tainted (Shopify CDN sends ACAO:*).
    No crossorigin attribute or markup change required on the rendered <img>.
  - Pure progressive enhancement: any failure (no support, network, CORS) leaves
    the element unmarked, and the section's own color-role fallback stays in
    effect. Nothing ever breaks; worst case is "no adaptive flip".
  - Lazy images: we wait for the <img> load so currentSrc is resolved.
  - Theme-editor safe: re-scans on shopify:section:load.
*/
(function () {
  'use strict';

  // Mean luminance above this (0..1) => light image => dark text.
  var THRESHOLD = 0.6;

  function meanLuminance(bitmap) {
    var w = 24, h = 24;
    // Sample the bottom half, where overlay captions/titles usually live.
    var sy = Math.floor(bitmap.height * 0.5);
    var sh = bitmap.height - sy;
    if (sh <= 0) { sy = 0; sh = bitmap.height; }

    var canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(bitmap, 0, sy, bitmap.width, sh, 0, 0, w, h);
    var data;
    try { data = ctx.getImageData(0, 0, w, h).data; }
    catch (e) { return null; } // tainted canvas guard

    var sum = 0, n = 0;
    for (var i = 0; i < data.length; i += 4) {
      // sRGB relative-luminance approximation
      sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
      n++;
    }
    return n ? sum / n : null;
  }

  function decide(el, lum) {
    if (lum === null) return;
    el.setAttribute('data-contrast', lum > THRESHOLD ? 'dark' : 'light');
  }

  function process(el) {
    if (el.__rbkMediaDone) return;
    var img = el.querySelector('img');
    if (!img) return;

    var run = function () {
      var url = img.currentSrc || img.src;
      if (!url) return;
      el.__rbkMediaDone = true;

      if (!('fetch' in window) || !('createImageBitmap' in window)) return;

      fetch(url, { mode: 'cors', credentials: 'omit' })
        .then(function (r) { return r.ok ? r.blob() : Promise.reject(); })
        .then(function (blob) { return createImageBitmap(blob); })
        .then(function (bmp) {
          var lum = meanLuminance(bmp);
          if (bmp.close) bmp.close();
          decide(el, lum);
        })
        .catch(function () { /* graceful: leave fallback color in place */ });
    };

    if (img.complete && (img.currentSrc || img.src)) run();
    else img.addEventListener('load', run, { once: true });
  }

  function init(scope) {
    var root = (scope && scope.querySelectorAll) ? scope : document;
    var els = root.querySelectorAll('[data-rbk-media-target]');
    for (var i = 0; i < els.length; i++) process(els[i]);
  }

  if (document.readyState !== 'loading') init(document);
  else document.addEventListener('DOMContentLoaded', function () { init(document); });

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
